import { CategoryAttributes, ProductAttributes } from '@italist/grpc-proto/services/guardian_pb';
import { Product } from 'models/product';
import Departments from 'models/departments';
import { getProductFilteredCategories } from 'shared/helpers/product';

/* eslint-disable @typescript-eslint/camelcase */

type GetProductTrackingData = (
  product: Product,
  currencySymbol: string,
  options: {
    selectedSize?: string;
    userFacingDepartment: string;
  },
) => {
  categoryDepartment: Partial<CategoryAttributes.AsObject>;
  product: Partial<ProductAttributes.AsObject>;
};

export const getProductTrackingData: GetProductTrackingData = (product: Product, currencySymbol: string, options) => {
  let department = options.userFacingDepartment.toLowerCase();
  if (Departments.getKidsDepartmentsInternalNames().includes(department)) {
    department = 'kids';
  }

  const categoriesFilteredWithDepartment = getProductFilteredCategories(product, options.userFacingDepartment);
  const departmentNames = [
    Departments.getWomenName(),
    Departments.getMenName(),
    Departments.getBabyBoysName(),
    Departments.getBabyGirlsName(),
    Departments.getBoysName(),
    Departments.getGirlsName(),
  ];
  const topLevelCategory = [...categoriesFilteredWithDepartment]
    .reverse()
    .find(category => !departmentNames.includes(category.name)).name;

  const bottomLevelCategory = product.category.name;

  const categoryTrack: ReturnType<GetProductTrackingData>['categoryDepartment'] = {
    category: topLevelCategory,
    department,
    subcategory: bottomLevelCategory !== topLevelCategory ? bottomLevelCategory : null,
  };

  const priceReducedAfterTax = product.price.reducedAfterTax / 100.0;
  const priceBaseAfterTax = product.price.baseAfterTax / 100.0;
  const onSale = priceReducedAfterTax !== priceBaseAfterTax;

  const productTrack: ReturnType<GetProductTrackingData>['product'] = {
    brand: product.brand.name,
    displayedDiscountPrice: onSale ? priceReducedAfterTax : null,
    displayedOriginalPrice: priceBaseAfterTax,
    localCurrency: currencySymbol,
    onSale,
    productId: product.productId,
    productVersionId: product.productVersionId,
    season: product.formattedSeason,
    store: product.store.storeId,
    title: product.model,
  };

  if (product.images) {
    productTrack.imageCount = product.images.length;
  }

  if (product.priceInAlternativeCurrenciesList && product.priceInAlternativeCurrenciesList.length > 0) {
    const approxPrice = product.priceInAlternativeCurrenciesList[0];
    const formattedApproxPrice = approxPrice.reducedAfterTax / 100.0;

    productTrack.approxPrice = `${formattedApproxPrice} ${approxPrice.currency}`;
  }

  if (options.selectedSize) {
    productTrack.productVersionOptionId = options.selectedSize;
  }

  return {
    categoryDepartment: categoryTrack,
    product: productTrack,
  };
};
